import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { space, device } from '@ticketswap/solar'
import { useViewer } from '../ViewerContext'
import { useLanguage } from '../LanguageContext'
import { track } from '~/utils/analytics'
import { APP_DOWNLOAD_CLICKED } from '~/lib/tracking-events'
import appStoreDe from './app-store-de.svg'
import appStoreEn from './app-store-en.svg'
import appStoreEs from './app-store-es.svg'
import appStoreFr from './app-store-fr.svg'
import appStoreHu from './app-store-hu.svg'
import appStoreNl from './app-store-nl.svg'
import appStorePt from './app-store-pt.svg'
import googlePlayDe from './google-play-de.svg'
import googlePlayEn from './google-play-en.svg'
import googlePlayEs from './google-play-es.svg'
import googlePlayFr from './google-play-fr.svg'
import googlePlayHu from './google-play-hu.svg'
import googlePlayNl from './google-play-nl.svg'
import googlePlayPt from './google-play-pt.svg'

const List = styled.ul`
  display: flex;
  flex-direction: column;

  @media ${device.tablet} {
    flex-direction: row;
  }

  li:not(:first-of-type) {
    margin-top: ${space[8]};

    @media ${device.tablet} {
      margin-top: 0;
      margin-left: ${space[16]};
    }
  }

  img {
    vertical-align: middle;
    max-width: 100%;
    max-height: ${space[48]};
  }
`

const Image = styled.img`
  height: 60px;
`

export function AppStoreBadge() {
  const language = useLanguage()

  function getImageUrl() {
    switch (language) {
      case 'de':
        return appStoreDe
      case 'es':
        return appStoreEs
      case 'fr':
        return appStoreFr
      case 'hu':
        return appStoreHu
      case 'nl':
        return appStoreNl
      case 'pt':
        return appStorePt
      default:
        return appStoreEn
    }
  }

  return <Image src={getImageUrl()} loading="lazy" alt="App Store" />
}

export function GooglePlayBadge() {
  const language = useLanguage()

  function getImageUrl() {
    switch (language) {
      case 'de':
        return googlePlayDe
      case 'es':
        return googlePlayEs
      case 'fr':
        return googlePlayFr
      case 'hu':
        return googlePlayHu
      case 'nl':
        return googlePlayNl
      case 'pt':
        return googlePlayPt
      default:
        return googlePlayEn
    }
  }

  return <Image src={getImageUrl()} loading="lazy" alt="Google Play" />
}

export function AppStoreLink({ children, trackingContext, ...props }) {
  return (
    <a
      href={`https://apps.apple.com/app/apple-store/id932337449?pt=76205801&ct=${encodeURIComponent(
        trackingContext.toLowerCase()
      )}&mt=8`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'App Store',
          trigger: trackingContext,
        })
      }
      {...props}
    >
      {children}
    </a>
  )
}

AppStoreLink.defaultProps = {
  trackingContext: 'General',
}

export function GooglePlayLink({ children, trackingContext, ...props }) {
  return (
    <a
      href={`https://play.google.com/store/apps/details?id=com.ticketswap.ticketswap&referrer=utm_source%ticketswap%26utm_medium%3Dinternal%26utm_term%3D${encodeURIComponent(
        trackingContext.toLowerCase()
      )}`}
      onClick={() =>
        track(APP_DOWNLOAD_CLICKED, {
          store: 'Google Play',
          trigger: trackingContext,
        })
      }
      {...props}
    >
      {children}
    </a>
  )
}

GooglePlayLink.defaultProps = {
  trackingContext: 'General',
}

function AppStoreBadges({ onClick, trackingContext, ...props }) {
  const { deviceInfo } = useViewer()
  const { isDesktop, isMobile, isIOS, isApp, isAndroid } = deviceInfo
  const showAppStoreLink = isDesktop || ((isMobile || isApp) && isIOS)
  const showGooglePlayLink = isDesktop || ((isMobile || isApp) && isAndroid)

  return (
    <List {...props}>
      {showAppStoreLink && (
        <li>
          <AppStoreLink trackingContext={trackingContext}>
            <AppStoreBadge />
          </AppStoreLink>
        </li>
      )}
      {showGooglePlayLink && (
        <li>
          <GooglePlayLink trackingContext={trackingContext}>
            <GooglePlayBadge />
          </GooglePlayLink>
        </li>
      )}
    </List>
  )
}

AppStoreBadges.defaultProps = {
  onClick: () => {},
  trackingContext: 'General',
}

AppStoreBadges.propTypes = {
  onClick: PropTypes.func,
  trackingContext: PropTypes.string,
}

export default AppStoreBadges
